import { ButtonLink, RadioGroup } from '@madpaws/design-system';
import { FieldArray, useFormikContext } from 'formik';
import getConfig from 'next/config';
import React from 'react';

import { MAIN_PET_TYPE_NAMES, PET_TYPE_NAMES } from '~/common/constants/app';

import styles from './ExpandedPetTypeField.module.css';
import { DOG_TYPE_OPTIONS, MAIN_PET_TYPE_OPTIONS, OTHER_PET_OPTIONS } from './constants';

import type { Options, RadioGroupOnChange } from '@madpaws/design-system';
import type { ReactElement } from 'react';
import type { ExpandedPet, SearchFilters } from '~/common/types/search';

const ACCEPTED_PET_ICON_SIZES = {
  width: 50,
  height: 50,
};

const { publicRuntimeConfig } = getConfig();

export const ExpandedPetTypeField = (): ReactElement => {
  const { setFieldValue, values } = useFormikContext<SearchFilters>();

  const getOptions = (options: Options): Options =>
    options.map((option) => ({
      ...option,
      icon: (
        <img
          {...ACCEPTED_PET_ICON_SIZES}
          alt={`${option.label} icon`}
          loading="lazy"
          src={`${publicRuntimeConfig.staticPath}/images/icons/petTypes/${option.value}.svg`}
        />
      ),
    }));

  const handleMainTypeChange = (index: number) => (event: RadioGroupOnChange): void => {
    setFieldValue(`pets[${index}].type`, event.target.value.value);
    setFieldValue(
      `pets[${index}].dogType`,
      event.target.value.value === MAIN_PET_TYPE_NAMES.DOG
        ? PET_TYPE_NAMES.SMALL_DOG
        : event.target.value.value === MAIN_PET_TYPE_NAMES.DOG
        ? PET_TYPE_NAMES.BIRD
        : ''
    );
    setFieldValue(
      `pets[${index}].otherType`,
      event.target.value.value === MAIN_PET_TYPE_NAMES.OTHER ? PET_TYPE_NAMES.BIRD : ''
    );
  };

  const handleDogTypeChange = (index: number) => (event: RadioGroupOnChange): void => {
    setFieldValue(`pets[${index}].dogType`, event.target.value.value);
  };

  const handleOtherTypeChange = (index: number) => (event: RadioGroupOnChange): void => {
    setFieldValue(`pets[${index}].otherType`, event.target.value.value);
  };

  // eslint-disable-next-line react/display-name
  const renderPetType = (_: ExpandedPet, index: number): ReactElement => (
    <div className={styles.petRow} key={index}>
      <div>
        <RadioGroup
          isTileWithImageCompressed
          legend={
            index === 0
              ? 'Pet, service, location & dates — let’s find your match'
              : 'Who else needs looking after?'
          }
          name={`pets[${index}].type`}
          onChange={handleMainTypeChange(index)}
          options={getOptions(MAIN_PET_TYPE_OPTIONS)}
          value={{ value: values.pets?.[index].type ?? '' }}
          variant="tiledWithImage"
        />

        {values.pets?.[index].type === MAIN_PET_TYPE_NAMES.DOG && (
          <div className={styles.dogType}>
            <RadioGroup
              isTileWithImageCompressed
              legend="What size?"
              name={`pets[${index}].dogType`}
              onChange={handleDogTypeChange(index)}
              options={getOptions(DOG_TYPE_OPTIONS)}
              value={{ value: values.pets?.[index].dogType as string }}
              variant="tiled"
            />
          </div>
        )}

        {values.pets?.[index].type === MAIN_PET_TYPE_NAMES.OTHER && (
          <div className={styles.otherType}>
            <RadioGroup
              isTileWithImageCompressed
              legend="Other types"
              name={`pets[${index}].otherType`}
              onChange={handleOtherTypeChange(index)}
              options={getOptions(OTHER_PET_OPTIONS)}
              value={{ value: values.pets?.[index].otherType as string }}
              variant="tiled"
            />
          </div>
        )}
      </div>
    </div>
  );
  return (
    <>
      <FieldArray name="pets">
        {({ push, remove }): ReactElement => (
          <div className={styles.root}>
            {values.pets?.map(renderPetType)}
            <div className={styles.petButtons}>
              <ButtonLink onClick={(): void => push({ type: '' })}>Add another pet +</ButtonLink>
              {values.pets && values.pets.length > 1 && (
                <ButtonLink onClick={(): void => values.pets && remove(values.pets.length - 1)}>
                  Remove pet
                </ButtonLink>
              )}
            </div>
          </div>
        )}
      </FieldArray>
    </>
  );
};
